import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Send, Loader2, MessageSquare, ChevronDown, Download } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { chatApi, type ChatSession } from '@/api/chat'
import { useChat } from '@/hooks/useChat'
import { Button } from '@/components/ui/button'
import { formatRelativeDate } from '@/lib/utils'

interface Props {
  projectId: string
}

/** Render message content with clickable citation chips for [filename.ext] references. */
function renderContentWithCitations(
  content: string,
  citedDocumentIds: string[] | null,
  projectId: string
) {
  const citationPattern = /\[([^\]]+\.[a-zA-Z]{2,5})\]/g
  const parts: (string | JSX.Element)[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = citationPattern.exec(content)) !== null) {
    // Text before the citation
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index))
    }
    const filename = match[1]
    parts.push(
      <a
        key={`cite-${match.index}`}
        href={`/api/projects/${projectId}/documents/download?filename=${encodeURIComponent(filename)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded px-1.5 py-0.5 mx-0.5 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors cursor-pointer"
        title={`Open ${filename}`}
      >
        {filename}
      </a>
    )
    lastIndex = match.index + match[0].length
  }

  // Remaining text after last citation
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex))
  }

  return parts.length > 0 ? parts : content
}

export default function ChatAssistant({ projectId }: Props) {
  const queryClient = useQueryClient()
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const [contextMode, setContextMode] = useState<'documents' | 'extracted_data' | 'both'>('documents')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Fetch available models for the plan
  const { data: planModels = [] } = useQuery({
    queryKey: ['chat-plan-models'],
    queryFn: () => chatApi.getPlanModels(),
  })

  // Set default model when plan models load
  useEffect(() => {
    if (planModels.length > 0 && !selectedModel) {
      const defaultModel = planModels.find((m) => m.is_default) || planModels[0]
      setSelectedModel(defaultModel.model_name)
    }
  }, [planModels, selectedModel])

  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ['chat-sessions', projectId],
    queryFn: () => chatApi.listSessions(projectId),
  })

  const createSessionMutation = useMutation({
    mutationFn: () => chatApi.createSession(projectId, { selected_model: selectedModel || undefined }),
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions', projectId] })
      setActiveSessionId(session.id)
    },
  })

  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => chatApi.deleteSession(projectId, sessionId),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions', projectId] })
      if (activeSessionId === deletedId) setActiveSessionId(null)
    },
  })

  const { messages, sendMessage, isStreaming } = useChat(projectId, activeSessionId)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = () => {
    if (!input.trim() || isStreaming) return
    sendMessage(input.trim(), selectedModel || undefined, contextMode)
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex h-full">
      {/* Session sidebar */}
      <div className="w-64 border-r flex flex-col bg-muted/20">
        <div className="p-3 border-b">
          <Button
            size="sm"
            className="w-full"
            onClick={() => createSessionMutation.mutate()}
            disabled={createSessionMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {sessionsLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No chats yet</div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                onClick={() => setActiveSessionId(session.id)}
                className={`group flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-accent text-sm ${
                  activeSessionId === session.id ? 'bg-accent' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">
                    {session.title || 'New conversation'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatRelativeDate(session.updated_at)}</span>
                    {session.message_count > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <MessageSquare className="h-3 w-3" />
                        {session.message_count}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (window.confirm('Delete this chat session?')) {
                      deleteSessionMutation.mutate(session.id)
                    }
                  }}
                  className="ml-1 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete session"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        {!activeSessionId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Start a conversation</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Ask questions about the documents in this project
            </p>
            <Button onClick={() => createSessionMutation.mutate()}>
              <Plus className="h-4 w-4 mr-1" />
              New Chat
            </Button>
          </div>
        ) : (
          <>
            {/* Export button */}
            {messages.length > 0 && activeSessionId && (
              <div className="flex justify-end px-4 pt-2">
                <button
                  onClick={() => chatApi.exportChat(projectId, activeSessionId, 'pdf')}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  title="Export chat"
                >
                  <Download className="h-3 w-3" /> Export
                </button>
              </div>
            )}
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-8">
                  Ask a question about your documents...
                </div>
              )}
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg px-4 py-2 text-sm ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted border'
                    }`}
                  >
                    <div>
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_table]:text-xs [&_th]:p-1.5 [&_td]:p-1.5 [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-800 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-xs [&_code]:text-xs">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <div>
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                          {msg.context_mode && msg.context_mode !== 'documents' && (
                            <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-primary-foreground/20 text-primary-foreground/70">
                              {msg.context_mode === 'extracted_data' ? 'Extracted Data' : 'Both'}
                            </span>
                          )}
                        </div>
                      )}
                      {(msg as { isStreaming?: boolean }).isStreaming && (
                        <span className="inline-block w-1 h-4 bg-current animate-pulse ml-0.5" />
                      )}
                    </div>
                    {msg.created_at && (
                      <p className={`text-[10px] mt-1 ${
                        msg.role === 'user' ? 'text-primary-foreground/60' : 'text-muted-foreground'
                      }`}>
                        {formatRelativeDate(msg.created_at)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area with model selector and context mode toggle */}
            <div className="border-t p-4">
              {/* Context mode + Model selector in one line */}
              <div className="flex items-center gap-1 mb-2">
                <span className="text-xs text-muted-foreground mr-1">Context:</span>
                {([
                  { value: 'documents', label: 'Documents' },
                  { value: 'extracted_data', label: 'Extracted Data' },
                  { value: 'both', label: 'Both' },
                ] as const).map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => setContextMode(mode.value)}
                    disabled={isStreaming}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      contextMode === mode.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground'
                    } disabled:opacity-50`}
                  >
                    {mode.label}
                  </button>
                ))}
                {planModels.length > 0 && (
                  <div className="relative ml-auto">
                    <select
                      value={selectedModel || ''}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      disabled={isStreaming}
                      className="appearance-none text-xs bg-muted border border-input rounded-md pl-2 pr-6 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                    >
                      {planModels.map((m) => (
                        <option key={m.model_name} value={m.model_name}>
                          {m.display_name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your documents..."
                  rows={1}
                  className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  disabled={isStreaming}
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || isStreaming}
                  size="icon"
                >
                  {isStreaming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
